// Stuck-fetch reporting, driven off the SAME `QueryCache.subscribe` feed as the
// perf spans in `perf.native` / `perf.web`.
//
// WHY THIS LIVES HERE rather than in a hook: `QueryCache.subscribe` is TanStack
// Query's officially-supported observability surface, and it is the only one that
// sees EVERY query — suspense or not, prefetch or on-demand, with or without a
// mounted observer. An earlier version of this was a `useWatchStuckRefetch` hook
// called from inside `usePrefetchedSuspenseQuery`, which meant a second mechanism
// to maintain and coverage limited to suspense queries. The cache subscription
// already tracks fetch start/end per query hash for spans; the watchdog is the
// same two edges, so it belongs on the same feed.
//
// WHY NOT A LIBRARY: TanStack deliberately has no query timeout — the maintainers
// rejected it (discussion #4652: "a timeout doesn't seem like a good fit because
// queries can take longer, can fail and then have retries"), and their answer is
// `AbortSignal` inside the `queryFn`. That is the right call for ABORTING, but the
// requirement here is the opposite: OBSERVE a fetch that never settles WITHOUT
// changing what happens. Aborting would mask the bug. Sentry's `idleTimeout` /
// `deadlineTimeout` only cancel spans to bound trace length, which reports nothing.
//
// The failure this exists for: with `retry: false` on the root client, a query that
// ERRORS rejects into an error boundary — visible and reported. A query that never
// SETTLES produces no error, no rejection, no event; the UI just keeps showing a
// spinner (or, on the Stats tab, a chevron replaced by one). This is the only
// instrument that sees that state.

/**
 * How long a single fetch may run before it is anomalous.
 *
 * Well above a healthy local-SQLite read (the DB watchdog flags 1s) and above a
 * normal remote round-trip, so this fires on pathology rather than variance.
 */
const STUCK_FETCH_THRESHOLD_MS = 8000;

/** Bounded geometric escalation, matching the DB and async-step watchdogs. */
const STUCK_REESCALATION_MS = [20_000, 60_000] as const;

const STUCK_FINGERPRINT = "query-fetch-stuck";

export interface StuckQueryReport {
  elapsedMs: number;
  /** Whether the query already had data — i.e. a background refetch, not a first load. */
  isBackgroundRefetch: boolean;
  queryHash: string;
  /** Short stable key prefix, used for grouping. */
  queryLabel: string;
}

/**
 * How the report reaches Sentry. Injected rather than imported: `captureSentryException`
 * lives in the platform-specific `sentry-setup.*` files, which import this module's
 * consumers — importing it back here would close a cycle. The caller (each `perf.*`
 * variant) already has the right one for its platform.
 */
export type CaptureStuckQuery = (error: Error, options: Record<string, unknown>) => void;

export function reportStuckQuery(
  capture: CaptureStuckQuery,
  { elapsedMs, isBackgroundRefetch, queryHash, queryLabel }: StuckQueryReport,
): void {
  const error = new Error(
    `${isBackgroundRefetch ? "Background refetch" : "Fetch"} still running after ${elapsedMs}ms: ${queryLabel}`,
  );
  error.name = "StuckQueryWatchdog";

  capture(error, {
    contexts: {
      "han.query": {
        elapsedMs,
        // A background refetch keeps stale data on screen, so no Suspense boundary
        // re-suspends and no other watchdog is armed — that case is invisible without
        // this flag to tell it apart from a first load.
        isBackgroundRefetch,
        queryHash,
        state: "in-flight",
      },
    },
    // Group by query label so one recurring stuck query is one issue, not one per
    // duration bucket or per id-bearing key tail.
    fingerprint: [STUCK_FINGERPRINT, queryLabel],
    level: "error",
    tags: {
      "han.query.key": queryLabel,
      "han.query.refetch": String(isBackgroundRefetch),
      "han.query.stuck": "true",
    },
  });
}

/**
 * Timers for one in-flight fetch. Armed when a query enters `fetching`, cleared
 * when it settles or is evicted.
 *
 * Armed at START, not derived on finish: a fetch that never settles never reaches
 * its own completion handler, so an after-the-fact measurement can never see the
 * case this exists for.
 */
export function armStuckQueryTimers(
  capture: CaptureStuckQuery,
  getReport: (elapsedMs: number) => StuckQueryReport,
): () => void {
  const startTime = Date.now();
  const timers: ReturnType<typeof setTimeout>[] = [];

  const arm = (delayMs: number): void => {
    const timer = setTimeout(() => {
      try {
        reportStuckQuery(capture, getReport(Date.now() - startTime));
      } catch {
        // Instrumentation must never surface as a query failure.
      }
    }, delayMs);
    // Never keep a Node/Jest host alive for a diagnostic timer.
    const unref: unknown =
      typeof timer === "object" && timer !== null ? Reflect.get(timer, "unref") : undefined;
    if (typeof unref === "function") {
      Reflect.apply(unref, timer, []);
    }
    timers.push(timer);
  };

  arm(STUCK_FETCH_THRESHOLD_MS);
  for (const extraDelayMs of STUCK_REESCALATION_MS) {
    arm(STUCK_FETCH_THRESHOLD_MS + extraDelayMs);
  }

  return () => {
    for (const timer of timers) {
      clearTimeout(timer);
    }
  };
}
